import React, { useMemo, useState } from 'react';
import { SearchIcon, ChevronDown, X } from 'lucide-react';
import { Protocol, PROTOCOLS } from '../data/protocols';
interface ProtocolListProps {
  onSelectProtocol: (protocol: Protocol) => void;
  searchQuery: string;
}
type SearchField = 'all' | 'name' | 'category' | 'tcmPattern' | 'clinical' | 'points';
const SEARCH_FIELD_LABELS: Record<SearchField, string> = {
  all: 'All Fields',
  name: 'Protocol Name',
  category: 'Category',
  tcmPattern: 'TCM Pattern',
  clinical: 'Clinical Picture',
  points: 'Acupoints'
};
function matchesField(protocol: Protocol, query: string, field: SearchField): boolean {
  const q = query.toLowerCase();
  switch (field) {
    case 'name':
      return protocol.name.toLowerCase().includes(q);
    case 'category':
      return protocol.category.toLowerCase().includes(q);
    case 'tcmPattern':
      return protocol.tcmPattern.toLowerCase().includes(q);
    case 'clinical':
      return protocol.indicationSummary.toLowerCase().includes(q) || protocol.remarks.toLowerCase().includes(q);
    case 'points':
      return protocol.points.some(p => p.points.toLowerCase().includes(q) || p.pointSet.toLowerCase().includes(q) || p.note.toLowerCase().includes(q));
    case 'all':
    default:
      return protocol.name.toLowerCase().includes(q) || protocol.category.toLowerCase().includes(q) || protocol.tcmPattern.toLowerCase().includes(q) || protocol.indicationSummary.toLowerCase().includes(q) || protocol.remarks.toLowerCase().includes(q) || protocol.points.some(p => p.points.toLowerCase().includes(q) || p.pointSet.toLowerCase().includes(q) || p.note.toLowerCase().includes(q));
  }
}
export function ProtocolList({
  onSelectProtocol,
  searchQuery
}: ProtocolListProps) {
  const [searchField, setSearchField] = useState<SearchField>('all');
  const [showFieldDropdown, setShowFieldDropdown] = useState(false);
  const [localSearch, setLocalSearch] = useState('');
  const effectiveSearch = searchQuery || localSearch;
  const {
    groupedProtocols,
    totalMatches
  } = useMemo(() => {
    const filtered = PROTOCOLS.filter(p => matchesField(p, effectiveSearch, searchField));
    const groups: Record<string, Protocol[]> = {};
    filtered.forEach(p => {
      if (!groups[p.category]) {
        groups[p.category] = [];
      }
      groups[p.category].push(p);
    });
    const sortedGroups = Object.keys(groups).sort().reduce((acc, key) => {
      acc[key] = groups[key];
      return acc;
    }, {} as Record<string, Protocol[]>);
    return {
      groupedProtocols: sortedGroups,
      totalMatches: filtered.length
    };
  }, [effectiveSearch, searchField]);
  return <div className="animate-in fade-in duration-300" data-id="element-110">
      {/* Header */}
      <div className="bg-white rounded-xl p-6 mb-6 border border-gray-100 shadow-sm" data-id="element-111">
        <h2 className="text-xl md:text-2xl font-semibold text-[#8B1538] mb-2" data-id="element-112">
          All Protocols
        </h2>
        <p className="text-gray-600 text-sm mb-4" data-id="element-113">
          Browse all {PROTOCOLS.length} protocols organized by category
        </p>

        {/* Search Filter */}
        <div className="flex flex-col sm:flex-row gap-3" data-id="element-114">
          <div className="relative flex-1" data-id="element-115">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-116" />
            <input type="text" placeholder="Filter protocols..." value={localSearch} onChange={e => setLocalSearch(e.target.value)} className="w-full pl-10 pr-8 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#8B1538]/20 focus:border-[#8B1538]" data-id="element-117" />
            {localSearch && <button onClick={() => setLocalSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-118">
                <X className="h-4 w-4" data-id="element-119" />
              </button>}
          </div>
          <div className="relative" data-id="element-120">
            <button onClick={() => setShowFieldDropdown(!showFieldDropdown)} className="flex items-center justify-between gap-2 px-4 py-2.5 text-sm bg-[#FEF2F2] border border-[#FECACA] rounded-lg text-[#8B1538] hover:bg-[#FEE2E2] transition-colors min-w-[160px]" data-id="element-121">
              <span data-id="element-122">{SEARCH_FIELD_LABELS[searchField]}</span>
              <ChevronDown className="h-4 w-4" data-id="element-123" />
            </button>
            {showFieldDropdown && <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[160px]" data-id="element-124">
                {(Object.keys(SEARCH_FIELD_LABELS) as SearchField[]).map(field => <button key={field} onClick={() => {
              setSearchField(field);
              setShowFieldDropdown(false);
            }} className={`w-full text-left px-4 py-2 text-sm hover:bg-[#FEF2F2] transition-colors first:rounded-t-lg last:rounded-b-lg ${searchField === field ? 'bg-[#FEF2F2] text-[#8B1538] font-medium' : 'text-gray-700'}`} data-id="element-125">
                      {SEARCH_FIELD_LABELS[field]}
                    </button>)}
              </div>}
          </div>
        </div>

        {effectiveSearch && <div className="mt-3 text-sm text-[#8B1538]" data-id="element-126">
            Found <strong data-id="element-127">{totalMatches}</strong> protocol
            {totalMatches !== 1 ? 's' : ''} matching "{effectiveSearch}"
          </div>}
      </div>

      {/* Protocol Grid by Category */}
      <div className="space-y-6" data-id="element-128">
        {Object.entries(groupedProtocols).map(([category, protocols]) => <div key={category} data-id="element-129">
            <div className="flex items-center gap-3 mb-3" data-id="element-130">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide" data-id="element-131">
                {category}
              </h3>
              <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full" data-id="element-132">
                {protocols.length}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-133">
              {protocols.map(protocol => <div key={protocol.id} onClick={() => onSelectProtocol(protocol)} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm cursor-pointer hover:border-[#FECACA] hover:shadow-md transition-all duration-200" data-id="element-134">
                  <h4 className="font-medium text-[#8B1538] mb-2" data-id="element-135">
                    {protocol.name}
                  </h4>
                  <p className="text-xs text-gray-500 line-clamp-2" data-id="element-136">
                    {protocol.indicationSummary}
                  </p>
                </div>)}
            </div>
          </div>)}

        {Object.keys(groupedProtocols).length === 0 && <div className="text-center py-12" data-id="element-137">
            <SearchIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" data-id="element-138" />
            <p className="text-gray-500" data-id="element-139">
              No protocols found matching "{effectiveSearch}"
            </p>
            <button onClick={() => {
          setLocalSearch('');
          setSearchField('all');
        }} className="mt-2 text-sm text-[#8B1538] hover:underline" data-id="element-140">
              Clear filters
            </button>
          </div>}
      </div>
    </div>;
}